import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { ProjectTasksFiltersComponent } from '@project-modules/project-tasks/pages/project-tasks/components/project-tasks-filters/project-tasks-filters.component';
import { ListConfig } from '@shared/modules/list/interfaces/list-config.interface';
import { cloneDeep } from 'lodash';
import { DefaultListConfig } from '@shared/modules/list/configs/default-list.config';
import { ProjectTasksListConfig } from '@project-modules/project-tasks/shared/configs/project-tasks-list.config';
import { ProjectPreviewService } from '@project-modules/project-preview/services/project-preview.service';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { ListService } from '@shared/modules/list/services/list.service';
import { StorageService } from '@core/services/storage.service';
import { Config } from '@shared/configs/config';
import { ListTasksComponent } from '@shared/modules/list/components/list-tasks/list-tasks.component';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { PermissionsGroups } from '@core/permissions/permissions.group';
import { IProjectPreviewEvent } from '@project-modules/project-preview/interfaces/project-preview-event.interface';
import { ProjectPreviewEventType } from '@project-modules/project-preview/enums/projcet-preview-event-type.enum';
import { SettingsService } from '@modules/settings/shared/services/settings.service';
import { ActivatedRoute } from '@angular/router';
import { TaskType } from '@shared/enums/task-type.enum';
import { tap } from 'rxjs/operators';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { Task } from '@shared/models/task.model';
import { TaskGroup } from '@shared/models/task-group.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { ListTasksV2TaskStatusEvent } from '@shared/modules/list/components/list-tasks-v2/components/list-tasks-v2-group/list-tasks-v2-group.component';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { TaskController } from '@shared/controllers/task.controller';
import { SnackBarService } from '@core/services/snackbar.service';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { ProcessMessageTaskSidenavComponent } from '@modules/projects/modules/project-dashboard/pages/project-stage-tasks/components/process-message-task-sidenav/process-message-task-sidenav.component';
import { ListTasksV2TemplateAddEvent } from '@shared/modules/list/components/list-tasks-v2/components/list-tasks-v2-group/list-tasks-v2-group.component';
import { ProjectDashboardStepperSyncService } from '@modules/projects/modules/project-dashboard/services/project-dashboard-stepper-sync.service';
import { ProjectStageAPIService } from '@modules/projects/shared/services/project-stage-api.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { UIModule } from '@shared/modules/ui/ui.module';
import { ListModule } from '@shared/modules/list/list.module';
import { ProjectsModule } from '@modules/projects/projects.module';
import { ListTasksV2Component } from '@shared/modules/list/components/list-tasks-v2/list-tasks-v2.component';
import { ListUrlController } from '@shared/modules/list/controllers/list-url.controller';

@Component({
  styleUrls: ['./project-tasks.component.scss'],
  templateUrl: './project-tasks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SettingsService, ListService, ListUrlController],
  standalone: true,
  imports: [SharedModule, UIModule, ListModule, ProjectsModule, ListTasksV2Component]
})
export class ProjectTasksComponent implements OnInit {
  public readonly projectPreviewService = inject(ProjectPreviewService);
  public readonly projectTaskApiService = inject(ProjectTaskAPIService);
  public readonly listTaskService = inject(ListTaskService);
  public readonly listService = inject(ListService);
  private readonly storageService = inject(StorageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly projectStore = inject(ProjectStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly snackBarService = inject(SnackBarService);
  private readonly sidenavService = inject(SidenavService);
  private readonly projectStageApiService = inject(ProjectStageAPIService);
  private readonly stepperSyncService = inject(ProjectDashboardStepperSyncService);
  private readonly translateService = inject(TranslateService);

  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  ProjectTasksFiltersComponent = ProjectTasksFiltersComponent;
  PermissionsGroups = PermissionsGroups;
  taskSidenavCtrl: TaskSidenavController;
  config: ListConfig;
  taskType: TaskType;
  taskTypeName: string;

  project = computed<Project | null>(() => this.projectStore.project() || null);
  projectStage = computed<ProjectStage | null>(() => (this.project()?.stage as ProjectStage) || null);
  stageTitle = computed(() => {
    const stage = this.projectStage();
    return stage ? this.translateService.instant(`Projects.Stage.${stage}`) : '';
  });
  templateGroups = signal<ListGroupTask[]>([]);
  templateLoading = signal(false);

  @ViewChild('listTasksComponent') listTasksComponent: ListTasksComponent;

  constructor() {
    effect(
      () => {
        const project = this.project();
        if (!project?.id) return;
        this.initListConfig();
        if (this.listTasksComponent?.listTasksController) {
          this.listTasksComponent.listTasksController.projectApiTaskService = this.projectTaskApiService;
        }
        this.setCompleted();
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const projectId = this.project()?.id ?? null;
      const projectStage = this.projectStage();

      queueMicrotask(() => {
        if (!projectId || !projectStage) {
          this.templateGroups.set([]);
          this.templateLoading.set(false);
          return;
        }

        this.loadTemplateTasks(projectId, projectStage, true);
      });
    });
  }

  ngOnInit() {
    this.taskSidenavCtrl = new TaskSidenavController();
    this.taskType = this.activatedRoute.snapshot.data.type;
    this.taskTypeName = this.getTaskTypeName(this.taskType);
    this.initActionSubscription();
    this.listenListServiceEvents();
    this.listenProjectPreviewEvents();
  }

  private initActionSubscription(): void {
    this.projectPreviewService.actionEvent$
      .pipe(
        tap(() => this.createNewTask()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private resolveShowCompletedCheckbox(): number {
    const userPerson = this.storageService.UserPerson;
    return userPerson && userPerson.showDoneTasks ? 1 : 0;
  }

  listenProjectPreviewEvents() {
    this.projectPreviewService.projectServiceEventEmitter
      .pipe(
        tap(({ type }: IProjectPreviewEvent) => {
          switch (type) {
            case ProjectPreviewEventType.CHANGE_STAGE:
              this.listService.getRows();
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenListServiceEvents(): void {
    this.listTaskService.emitter
      .pipe(
        tap(({ type, data }) => {
          const projectId = this.project()?.id;
          const projectStage = this.projectStage();
          if (!projectId) return;

          switch (type) {
            case ListTaskEventType.UPDATE_TASK_LIST:
              this.refreshAllTasks(projectId, projectStage);
              return;
            case ListTaskEventType.ADD_TASK:
              if (data?.parentId || data?.task?.parentId) {
                this.refreshAllTasks(projectId, projectStage);
                return;
              }
              if (this.isProcessTaskEvent(data)) {
                this.loadTemplateTasks(projectId, projectStage, false);
              } else {
                this.listService.getRows();
              }
              return;
            case ListTaskEventType.ADD_SUB_TASK:
              this.refreshAllTasks(projectId, projectStage);
              return;
            case ListTaskEventType.UPDATE_TASK:
            case ListTaskEventType.REMOVE_TASK:
              if (this.isProcessTaskEvent(data)) {
                this.loadTemplateTasks(projectId, projectStage, false);
              } else {
                this.listService.getRows();
              }
              return;
            default:
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  createNewTask(): void {
    this.taskSidenavCtrl.addTask({
      projectID: this.project()?.id,
      taskType: this.taskType
    });
  }

  createNewTaskGroup(): void {
    this.taskSidenavCtrl.addTaskGroup(this.project()?.id, this.taskType);
  }

  onTemplateTaskStatusChanged(event: ListTasksV2TaskStatusEvent): void {
    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    if (!projectId || !projectStage) return;

    const ctrl = new TaskController(event.task);
    const completeSubtasks$ = event.subtasksCompleted ? ctrl.completeSubtasks(event.task) : of(null);

    completeSubtasks$
      .pipe(
        switchMap(() => ctrl.changeTaskStatus(event.task)),
        switchMap(() => this.projectStageApiService.getProjectStages(projectId)),
        tap((stages) => {
          this.stepperSyncService.updateProjectStages(projectId, stages || []);
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorStatusChange'));
          return of(null);
        }),
        finalize(() => this.loadTemplateTasks(projectId, projectStage, false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.snackBarService.success(this.getStatusChangedMessage(event.task));
      });
  }

  onTemplateTaskClicked(event: { task: Task; groupId: number; isProcessGroup: boolean }): void {
    if (!event?.isProcessGroup || String((event?.task as any)?.type || '') !== 'message') return;

    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    const task = event.task;
    if (!projectId || !projectStage || !task?.id) return;

    this.sidenavService.open(
      ProcessMessageTaskSidenavComponent,
      this.taskSidenavCtrl.getWideSidenavConfig(),
      {
        mode: 'send',
        projectId,
        stage: projectStage,
        sortOrder: Number(task.sortOrder || 1),
        parentId: task.parentId ?? null,
        groupId: event.groupId,
        mapperTaskId: task.id,
        task
      }
    );
  }

  onTemplateAddSelected(event: ListTasksV2TemplateAddEvent): void {
    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    if (!projectId || !projectStage) return;

    const group = event.group;
    const sortOrder =
      (group?.tasks?.length || 0) +
      (group?.completedTasks?.length || 0) +
      (group?.cancelledTasks?.length || 0) +
      1;

    if (event.type === 'message') {
      const mapperTaskId = Number(
        group?.tasks?.[0]?.id || group?.completedTasks?.[0]?.id || group?.cancelledTasks?.[0]?.id || 0
      );

      this.sidenavService.open(
        ProcessMessageTaskSidenavComponent,
        this.taskSidenavCtrl.getWideSidenavConfig(),
        {
          projectId,
          stage: projectStage,
          sortOrder,
          parentId: null,
          groupId: group?.taskGroup?.id || null,
          mapperTaskId: mapperTaskId > 0 ? mapperTaskId : null
        }
      );
      return;
    }

    this.taskSidenavCtrl.addTask({
      projectID: projectId,
      groupType: 'activity-template',
      taskType: event.type as TaskType,
      taskGroupId: group?.taskGroup?.id,
      extra: {
        type: event.type,
        isProcessTaskContext: true,
        activityTemplateTaskType: event.type,
        activityTemplateContext: {
          stage: projectStage,
          sortOrder,
          parentId: null
        }
      }
    });
  }

  private initListConfig(): void {
    this.config = cloneDeep(DefaultListConfig);
    const listConfig: ListConfig = {
      ...ProjectTasksListConfig,
      url: `${Config.API}/project/${this.project().id}/task`
    };
    this.config = Object.assign(this.config, listConfig);
    this.listService.data['projectId'] = this.project().id;
    this.cdr.detectChanges();
  }

  private setCompleted(): void {
    const showDoneTasks = this.resolveShowCompletedCheckbox();
    this.listService.setFilter('[show_completed]', showDoneTasks ? 1 : 0);
    if (this.listService.config) {
      this.listService.setPage(1);
      this.listService.getRows();
    }
  }

  onChangeFilter(): void {
    Object.keys(this.listService.filtersForm.value).forEach((key: string) => {
      let value = this.listService.filtersForm.value[key];
      Array.isArray(value) && !value.length ? (value = null) : null;
      this.listService.setFilter(key, value);
    });
    this.listService.getRows();
  }

  private getTaskTypeName(taskType: TaskType): string {
    switch (taskType) {
      case TaskType.TYPE_TASK:
        return 'tasks';
      case TaskType.TYPE_CHANGE:
        return 'changes';
      case TaskType.TYPE_CORRECTION:
        return 'corrections';
      case TaskType.TYPE_ORDER:
        return 'orders';
      default:
        return 'tasks';
    }
  }

  private loadTemplateTasks(
    projectId: number | null,
    stage: ProjectStage | null,
    withLoader: boolean = true
  ): void {
    if (!projectId || !stage) {
      this.templateGroups.set([]);
      return;
    }

    const openedTaskIds = this.collectOpenedTaskIds(this.templateGroups());

    if (withLoader) this.templateLoading.set(true);

    this.projectTaskApiService
      .getProjectActivityTasks(projectId, stage)
      .pipe(
        map((rows) => this.normalizeApiGroups(rows || [], true, openedTaskIds)),
        tap((groups) => this.templateGroups.set(groups)),
        catchError(() => {
          this.templateGroups.set([]);
          return of([]);
        }),
        finalize(() => {
          if (withLoader) this.templateLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private refreshAllTasks(projectId: number, projectStage: ProjectStage | null): void {
    this.loadTemplateTasks(projectId, projectStage, false);
    this.listService.getRows();
  }

  private normalizeApiGroups(
    rows: ListGroupTask[],
    isProcessGroup: boolean,
    openedTaskIds: Set<number> = new Set<number>()
  ): ListGroupTask[] {
    return rows
      .map((row) => {
        const taskGroup = new TaskGroup(row.taskGroup);

        const tasks = (row.tasks || [])
          .map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        const completedTasks = (row.completedTasks || [])
          .map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        const cancelledTasks = (row.cancelledTasks || [])
          .map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        return {
          ...row,
          data: {
            ...(row as any)?.data,
            isProcessGroup
          },
          taskGroup,
          tasks,
          completedTasks,
          cancelledTasks
        } as ListGroupTask;
      })
      .sort((a, b) => (a.taskGroup?.sortOrder || 0) - (b.taskGroup?.sortOrder || 0));
  }

  private normalizeTask(task: Task, taskGroup: TaskGroup, openedTaskIds: Set<number>): Task {
    const children = ((task as any)?.children || [])
      .map((child: Task) => this.normalizeTask(child, taskGroup, openedTaskIds))
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const normalizedTask = new Task({ ...(task as any), taskGroup, children } as any);
    const safeTaskId = Number(normalizedTask?.id || 0);
    const visibleChildren = (children || []).filter((child) => !child?.deleted);

    normalizedTask.children = children;
    normalizedTask.childrenCount = visibleChildren.length;
    normalizedTask.completedChildrenCount = visibleChildren.filter(
      (child) => child?.status === TaskStatus.COMPLETED
    ).length;

    if (safeTaskId > 0) normalizedTask.openedSubtasks = openedTaskIds.has(safeTaskId);
    else normalizedTask.openedSubtasks = !!normalizedTask.openedSubtasks;

    return normalizedTask;
  }

  private collectOpenedTaskIds(groups: ListGroupTask[]): Set<number> {
    const ids = new Set<number>();

    const collectFromTask = (task: Task): void => {
      const taskId = Number(task?.id || 0);
      if (taskId > 0 && !!task?.openedSubtasks) {
        ids.add(taskId);
      }
      (task?.children || []).forEach((child) => collectFromTask(child));
    };

    (groups || []).forEach((group) => {
      (group?.tasks || []).forEach((task) => collectFromTask(task));
      (group?.completedTasks || []).forEach((task) => collectFromTask(task));
      (group?.cancelledTasks || []).forEach((task) => collectFromTask(task));
    });

    return ids;
  }

  private isProcessTaskEvent(data: any): boolean {
    if (data?._isActivityTemplate || data?.task?._isActivityTemplate) {
      return true;
    }

    const taskLike = data?.task || data;
    const taskGroupId = Number(taskLike?.taskGroup?.id);
    if (!taskGroupId) return false;

    return this.templateGroups().some((group) => Number(group?.taskGroup?.id) === taskGroupId);
  }

  private getStatusChangedMessage(task: Task): string {
    const rawType = task?.type || 'task';
    const normalizedType = rawType === 'company-task' || rawType === 'client-task' ? 'task' : rawType;
    const actionKey = task?.status === TaskStatus.COMPLETED ? 'completed' : 'created';

    return this.translateService.instant('Tasks.successStatusChangeAction', {
      taskType: this.translateService.instant(`Tasks.TaskType.${normalizedType}`),
      action: this.translateService.instant(`Tasks.statusAction.${actionKey}`)
    });
  }
}
